const fnMap = new Map<string, ReturnType<typeof setTimeout>>()

/**
 * Debounce
 *
 * @param id Unique ID which is overridden if called before `ms`
 * @param fn Function to debounce
 * @param ms Time to wait
 * @returns function that cancels debounce
 */
export function debounceFn(id: string, fn: () => void, ms = 3000): () => void {
    if (fnMap.has(id)) {
        const timerID = fnMap.get(id)
        window.clearTimeout(timerID)
        fnMap.delete(id)
    }

    const timerID = setTimeout(() => {
        fn()
        fnMap.delete(id)
    }, ms)

    fnMap.set(id, timerID)

    return () => {
        clearTimeout(timerID)
        fnMap.delete(id)
    }
}

/**
 * Cancel
 */
export function debounceCancel(id: string) {
    const timerID = fnMap.get(id)
    if (timerID) {
        clearTimeout(timerID)
        fnMap.delete(id)
    }
}

// @ts-ignore
window.debounce = debounceFn

//
// MERGE QUEUE
//
//

type MergeRecord = {
    timer: ReturnType<typeof setTimeout>
    record: Record<string, unknown>
}
const merges = new Map<string, MergeRecord>()
/**
 * This solves the problem of things being debounced and then something
 * of higher priority pushing before the debounce can go through, cancelling the former.
 * This function makes it so that all merges are {...merged, ...new} with each other
 * when the timer runs out. When the timer runs out, it calls the passed function
 * on to the last object (which is an accumulation of all the previously
 * pending ones)
 *
 * A debounceMergeQueue with 0 delay will accumulate all pending changes to the requested
 * change so they all go together.
 *
 * @returns cancel function, but you can also cancel using {@link cancelDebounceMergeQueue}(id)
 */
export async function debounceMergeQueue<T extends Record<string, unknown>>(
    id: string,
    nextMergeObject: Partial<T>,
    saveMergedFunction: (s: Partial<T>) => Promise<void>,
    delay = 3000
) {
    // Merge it if there's one already
    if (merges.has(id)) {
        const { timer, record } = merges.get(id)!
        clearTimeout(timer)
        nextMergeObject = { ...record, ...nextMergeObject } // Append previous to current
        merges.delete(id)
    }

    if (delay == 0) {
        // Instant
        merges.delete(id)
        await saveMergedFunction(nextMergeObject)
    } else {
        const timer = setTimeout(() => {
            saveMergedFunction(nextMergeObject)
        }, delay)

        merges.set(id, {
            timer,
            record: nextMergeObject,
        })
    }

    return () => cancelDebounceMergeQueue(id)
}

export function cancelDebounceMergeQueue(id: string) {
    const { timer } = merges.get(id) || {}
    clearTimeout(timer)
    merges.delete(id)
}
